import React, { useEffect, useRef, useState } from "react";

interface GoogleMapProps extends google.maps.MapOptions {
  center?: google.maps.LatLngLiteral;
  zoom?: number;
  style: { [key: string]: string };
  onClick?: (e: google.maps.MapMouseEvent) => void;
  onIdle?: (map: google.maps.Map) => void;
  children?: React.ReactNode;
}

// Displays an interactive Google Map component
// The children prop is used to place markers on the map
const GoogleMap: React.FC<GoogleMapProps> = ({
  center = null,
  zoom = null,
  style,
  children = <></>,
  onClick = null,
  onIdle = null,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [map, setMap] = useState<google.maps.Map>();

  // Initialize a new map display with the center and zoom values that were passed in
  useEffect(() => {
    if (ref.current && !map) {
      setMap(new window.google.maps.Map(ref.current, { center, zoom }));
    }
  }, [ref, map, center, zoom]);

  // Clear existing listeners when a handler passed as a prop has been updated
  // More information: https://developers.google.com/maps/documentation/javascript/react-map
  useEffect(() => {
    if (map) {
      ["click", "idle"].forEach((eventName) =>
        google.maps.event.clearListeners(map, eventName)
      );

      if (onClick) {
        map.addListener("click", onClick);
      }

      if (onIdle) {
        map.addListener("idle", () => onIdle(map));
      }
    }
  }, [map, onClick, onIdle]);

  return (
    <>
      <div ref={ref} style={style} />
      {React.Children.map(children, (child) => {
        if (React.isValidElement(child)) {
          // set the map prop on the child component
          return React.cloneElement(child, { map } as { map: google.maps.Map });
        }
        return null;
      })}
    </>
  );
};

export default GoogleMap;
